import {
  IconButton,
  SingleSelectDropdown,
  Text,
  toast,
  UserAddOutlineIcon,
} from "@carbon6/c6-frontend-design";
import { useCallback, useMemo, type ReactNode } from "react";
import { useSearchParams } from "react-router";
import {
  getCurrentMe,
  isLastAdminError,
  listTrainingGroups,
  setMemberTier,
  type OrgMemberWorkspaceRef,
  type RegistryGroupRef,
  type TrainingGroup,
} from "@/api";
import type { UserRole } from "@/app/userRole";
import { hatcheryDropdownModes, hatcheryTextModes } from "@/app/theme";
import { GnbMenu } from "@/components/base";
import { ResourceBrowserLayout } from "@/components/browse";
import {
  FilterBar,
  FilterResultCount,
  FilterSearchInput,
  type FilterBarChip,
} from "@/components/filter";
import { HatTextModal } from "@/components/modal";
import {
  PageBase,
  type PageBaseContext,
  type PageBaseProps,
  type PageBaseTabs,
} from "@/page/base/PageBase";
import { BulkActionBar } from "@/components/table/BulkActionBar";
import bulkActionBarStyles from "@/components/table/BulkActionBar.module.css";
import { HatDataTable } from "@/components/table/HatDataTable";
import { WorkspaceSyncPanel } from "@/page/adminWorkspaceSync/WorkspaceSyncPanel";
import { GroupMappingModal, type GroupCandidateStatus } from "./GroupMappingModal";
import { GroupsManagerModal } from "./GroupsManagerModal";
import { createOrgMemberColumns } from "./orgMemberColumns";
import type { OrgMemberTableRow } from "./OrgMemberTableRow";
import { OrgMemberTableSource, type RegisteredFilter } from "./OrgMemberTableSource";
import { WorkspaceMappingModal } from "./WorkspaceMappingModal";
import {
  ALL_REGISTERED,
  EMPTY_MEMBER_FILTERS,
  REGISTERED_FILTER_OPTIONS,
  clearMemberFilterAxis,
  memberFilterChips,
  readMemberFilters,
  writeMemberFilters,
  type MemberFilterState,
} from "./memberFilters";
import { memberTierLabel } from "./memberTier";
import styles from "./AdminMembersPage.module.css";
import { tableStripError } from "@/components/table/tableStateMessages";

const tabSearchParamKey = "tab";

// 멤버 관리 콘텐츠 탭. 한 페이지에서 멤버 디렉터리와 workspace 동기화 확인(선점↔training live
// 대조)을 다룬다.
const adminMembersTabValues = ["members", "sync"] as const;
type AdminMembersTab = (typeof adminMembersTabValues)[number];
const defaultAdminMembersTab: AdminMembersTab = "members";

const adminMembersTabItems: PageBaseTabs["items"] = [
  { value: "members", label: "멤버" },
  { value: "sync", label: "동기화 확인" },
];

function normalizeAdminMembersTab(value: string | null): AdminMembersTab {
  return adminMembersTabValues.includes(value as AdminMembersTab)
    ? (value as AdminMembersTab)
    : defaultAdminMembersTab;
}

export function AdminMembersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = normalizeAdminMembersTab(searchParams.get(tabSearchParamKey));
  // 멤버 탭의 조회 축(검색·가입 여부)도 URL 이 소유한다(rules.md §17).
  const filters = useMemo(() => readMemberFilters(searchParams), [searchParams]);
  const handleFiltersChange = useCallback(
    (next: MemberFilterState) => {
      setSearchParams((prev) => writeMemberFilters(prev, next), { replace: true });
    },
    [setSearchParams],
  );

  // 탭 전환은 URL ?tab= 으로 보존한다(기본 탭이면 키 생략).
  const handleTabChange = useCallback(
    (nextTab: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (nextTab === defaultAdminMembersTab) {
          next.delete(tabSearchParamKey);
        } else {
          next.set(tabSearchParamKey, nextTab);
        }
        return next;
      });
    },
    [setSearchParams],
  );

  return (
    <AdminMembersPageView
      pageTitle="Members"
      currentMenu={GnbMenu.AdminMembers}
      requiredRoles={["admin"]}
      scrollable
      tabs={{
        items: adminMembersTabItems,
        value: tab,
        onValueChange: handleTabChange,
        ariaLabel: "멤버 관리 탭",
      }}
      // 필터 바가 헤더·본문 경계를 이미 만드는 탭에서는 tabpanel 상단 여백을 추가하지 않는다.
      bodyFlushTop={tab === "members"}
      filters={filters}
      onFiltersChange={handleFiltersChange}
    />
  );
}

type AdminMembersPageViewProps = PageBaseProps & {
  filters: MemberFilterState;
  onFiltersChange: (next: MemberFilterState) => void;
};

// 인라인 드롭다운에서 고른 등급 변경 후보(확인 모달 대기). 확정할 때까지 행에는 반영하지 않는다.
type PendingTierChange = {
  member: OrgMemberTableRow;
  fromTier: UserRole;
  toTier: UserRole;
};

type AdminMembersPageState = {
  pending: PendingTierChange | null;
  saving: boolean;
  // 매핑 컬럼 클릭으로 열린 Edit 모달 대상(null=닫힘).
  groupModalMember: OrgMemberTableRow | null;
  workspaceModalMember: OrgMemberTableRow | null;
  // Registry 그룹 관리 모달(그룹 CRUD + GitHub 팀 매핑) 열림 여부.
  groupsManagerOpen: boolean;
};

// org 전체 멤버(가입+미가입) 디렉터리 + 등급·팀 매핑 관리(단일 페이지). 가드레일:
//   - 본인 행 등급 변경 금지.
//   - 마지막 admin 강등 차단(BE 409).
//   - 변경 확인 모달.
//   - admin 부여 시 danger 톤 경고.
class AdminMembersPageView extends PageBase<AdminMembersPageViewProps, AdminMembersPageState> {
  // 첫 조건은 생성 시 소스에 전달해 initialize 한 번으로 처리한다. 그 뒤의 변경은
  // componentDidUpdate 가 처리한다.
  private readonly source = new OrgMemberTableSource({
    resolveErrorMessage,
    initialSearch: this.props.filters.search,
    initialRegisteredFilter: this.props.filters.registered,
  });
  // 본인 행 등급 변경을 막으려고 현재 로그인 멤버 uuid 를 보관한다.
  private currentMemberUuid: string | null = null;
  // 그룹 매핑 모달의 추가 드롭다운 후보(레지스트리 그룹 전체).
  private groups: TrainingGroup[] = [];
  // 후보를 받기 전과 받지 못한 경우를 빈 후보와 구분한다. 모달이 드롭다운 문구로 알린다.
  private groupsStatus: GroupCandidateStatus = "loading";
  // 매핑 모달에서 변이가 한 번이라도 성공했는지 기록한다. 닫을 때 정합 재조회 여부를 이 값으로 정한다.
  private mappingDirty = false;
  private disposed = false;

  state: AdminMembersPageState = {
    pending: null,
    saving: false,
    groupModalMember: null,
    workspaceModalMember: null,
    groupsManagerOpen: false,
  };

  protected override async initPage(): Promise<void> {
    this.disposed = false;
    this.source.subscribe(this.handleSourceChange);
    // me·그룹 조회 실패가 목록을 막지 않도록 분리한다. 실패하면 자기 보호·그룹 추가만 비활성이다.
    const [me, , groups] = await Promise.allSettled([
      getCurrentMe(),
      this.source.initialize(),
      listTrainingGroups(),
    ]);
    if (this.disposed) {
      return;
    }
    if (me.status === "fulfilled") {
      this.currentMemberUuid = me.value.member.uuid;
    }
    if (groups.status === "fulfilled") {
      this.groups = groups.value;
      this.groupsStatus = "ready";
    } else {
      this.groupsStatus = "error";
    }
    this.forceUpdate();
  }

  protected override disposePage(): void {
    this.disposed = true;
    this.source.dispose();
  }

  // URL 조건이 바뀌면(필터 바·칩·뒤로가기) 소스에 전달한다. setter 는 같은 값이면 no-op 이다.
  componentDidUpdate(prevProps: AdminMembersPageViewProps): void {
    const { filters } = this.props;
    if (filters.search !== prevProps.filters.search) {
      void this.source.setSearch(filters.search);
    }
    if (filters.registered !== prevProps.filters.registered) {
      void this.source.setRegisteredFilter(filters.registered);
    }
  }

  private readonly handleSourceChange = (): void => {
    if (!this.disposed) {
      this.forceUpdate();
    }
  };

  private readonly handleSearchCommit = (search: string): void => {
    this.props.onFiltersChange({ ...this.props.filters, search });
  };

  private readonly handleRegisteredFilterChange = (value: string): void => {
    this.props.onFiltersChange({ ...this.props.filters, registered: value as RegisteredFilter });
  };

  private readonly handleClearAllFilters = (): void => {
    this.props.onFiltersChange(EMPTY_MEMBER_FILTERS);
  };

  private readonly handleRefreshClick = (): void => {
    void this.source.reload();
  };

  private readonly handleEditGroups = (row: OrgMemberTableRow): void => {
    // 후보를 받지 못한 채로 모달을 열면 다시 받는다. 모달의 실패 문구가 이 경로를 안내한다.
    if (this.groupsStatus === "error") {
      this.groupsStatus = "loading";
      void this.reloadGroups();
    }
    this.setState({ groupModalMember: row });
  };

  private readonly handleEditWorkspaces = (row: OrgMemberTableRow): void => {
    this.setState({ workspaceModalMember: row });
  };

  private readonly handleCloseGroupModal = (): void => {
    this.setState({ groupModalMember: null });
    this.reloadIfMappingDirty();
  };

  private readonly handleCloseWorkspaceModal = (): void => {
    this.setState({ workspaceModalMember: null });
    this.reloadIfMappingDirty();
  };

  // 매핑(a축)이 바뀌면 캐시 행을 바로 교체한다. 모달이 열려 있어도 뒤의 표가 맞아야 한다.
  // 모달이 전달한 다음 목록이 곧 정답이다. 그래서 재조회를 기다리지 않는다.
  private readonly handleGroupsChanged = (registryGroups: readonly RegistryGroupRef[]): void => {
    const member = this.state.groupModalMember;
    if (member === null) {
      return;
    }
    this.mappingDirty = true;
    void this.source.applyRegistryGroups(member.githubUserId, registryGroups);
  };

  private readonly handleWorkspacesChanged = (
    workspaces: readonly OrgMemberWorkspaceRef[],
  ): void => {
    const member = this.state.workspaceModalMember;
    if (member === null) {
      return;
    }
    this.mappingDirty = true;
    void this.source.applyWorkspaces(member.githubUserId, workspaces);
  };

  // 닫을 때 한 번만 서버와 정합을 맞춘다. 정상이면 낙관적 패치와 같은 값이 온다. 값이 다르면
  // (동시 편집·b축 파급) 서버 값을 쓴다.
  private reloadIfMappingDirty(): void {
    if (!this.mappingDirty) {
      return;
    }
    this.mappingDirty = false;
    void this.source.reload();
  }

  private readonly handleOpenGroupsManager = (): void => {
    this.setState({ groupsManagerOpen: true });
  };

  private readonly handleCloseGroupsManager = (): void => {
    this.setState({ groupsManagerOpen: false });
  };

  // 그룹 CRUD·GitHub 팀 매핑(b축)이 바뀐 뒤 그룹 매핑 모달의 후보와 디렉터리(registry_groups)를
  // 갱신한다.
  private readonly handleGroupsManagerChanged = (): void => {
    void this.reloadGroups();
    void this.source.reload();
  };

  private async reloadGroups(): Promise<void> {
    try {
      const groups = await listTrainingGroups();
      if (!this.disposed) {
        this.groups = groups;
        this.groupsStatus = "ready";
        this.forceUpdate();
      }
    } catch {
      // 받아 둔 후보가 있으면 그 후보를 계속 쓴다. 받은 적이 없으면 실패로 남긴다. 그러면 모달이
      // 실패를 알리고, 다음에 열 때 다시 받는다.
      if (!this.disposed && this.groupsStatus !== "ready") {
        this.groupsStatus = "error";
        this.forceUpdate();
      }
    }
  }

  // 드롭다운에서 새 등급을 고르면 확인 모달을 연다. 아직 행에 반영하지 않는다.
  // 그래서 취소하면 원래 등급으로 자동으로 돌아간다.
  private readonly handleSelectTier = (row: OrgMemberTableRow, toTier: UserRole): void => {
    if (row.tier === null || toTier === row.tier || this.state.saving) {
      return;
    }
    this.setState({ pending: { member: row, fromTier: row.tier, toTier } });
  };

  private readonly handleCancelPending = (): void => {
    if (this.state.saving) {
      return;
    }
    this.setState({ pending: null });
  };

  private readonly handleConfirmPending = (): void => {
    const pending = this.state.pending;
    if (pending === null || pending.member.memberUuid === null || this.state.saving) {
      return;
    }
    const memberUuid = pending.member.memberUuid;
    this.setState({ saving: true });
    void setMemberTier(memberUuid, pending.toTier)
      .then((member) => {
        if (this.disposed) {
          return;
        }
        toast.success(
          `${member.display_name} 역할을 ${memberTierLabel(member.tier)}(으)로 변경했습니다.`,
        );
        this.setState({ pending: null, saving: false });
        void this.source.applyTier(memberUuid, member.tier);
      })
      .catch((error: unknown) => {
        if (this.disposed) {
          return;
        }
        // 마지막 admin 강등은 BE 가드가 막는다(409). 그 외는 일반 에러로 안내한다. 드롭다운은
        // controlled value=row.tier 다. 그래서 모달을 닫으면 자동으로 원래 등급으로 돌아간다.
        if (isLastAdminError(error)) {
          toast.error("마지막 관리자는 강등할 수 없습니다. 조직에 관리자가 최소 1명 필요합니다.");
        } else {
          toast.error(resolveErrorMessage(error));
        }
        this.setState({ pending: null, saving: false });
      });
  };

  // 제목 우측. Registry 그룹 관리(그룹 CRUD + GitHub 팀 매핑). 멤버 디렉터리 조건과 관계없는
  // 페이지 단위 관리 진입점이다. 그래서 헤더에 두고, 탭과 관계없이 연다.
  protected override renderHeaderActions(): ReactNode {
    return (
      <IconButton
        text="그룹 관리"
        variant="surface"
        disabled={this.source.getSnapshot().loadState === "loading"}
        onClick={this.handleOpenGroupsManager}
      >
        <UserAddOutlineIcon />
      </IconButton>
    );
  }

  // 탭 줄 아래 필터 바. 멤버 탭에서만 표시한다. 동기화 확인 탭은 자체 조회 축을 가진 패널이다.
  protected override renderBelowTabs(context: PageBaseContext): ReactNode {
    if (context.currentTab === "sync") {
      return null;
    }
    const { filters, onFiltersChange } = this.props;
    const chips: FilterBarChip[] = memberFilterChips(filters).map((chip) => ({
      key: chip.axis,
      label: chip.label,
      removeLabel: `${chip.label} 해제`,
      onRemove: () => onFiltersChange(clearMemberFilterAxis(filters, chip.axis)),
    }));
    return (
      <FilterBar
        ariaLabel="멤버 필터"
        search={
          <FilterSearchInput
            value={filters.search}
            ariaLabel="멤버 검색"
            placeholder="GitHub 로그인, GitHub 팀 또는 Registry 팀을 검색해 보세요."
            onCommit={this.handleSearchCommit}
          />
        }
        chips={chips}
        onClearAll={this.handleClearAllFilters}
      >
        <SingleSelectDropdown
          mode={hatcheryDropdownModes.filter}
          label="가입 여부"
          neutralValue={ALL_REGISTERED}
          value={filters.registered}
          options={REGISTERED_FILTER_OPTIONS}
          onValueChange={this.handleRegisteredFilterChange}
        />
      </FilterBar>
    );
  }

  protected override renderContent(context: PageBaseContext): ReactNode {
    // 그룹 관리 모달은 헤더 진입점과 짝이다. 그래서 탭 분기 밖에서 그린다. 동기화 확인 탭에서도 열린다.
    const groupsManagerModal = this.state.groupsManagerOpen ? (
      <GroupsManagerModal
        onClose={this.handleCloseGroupsManager}
        onChanged={this.handleGroupsManagerChanged}
      />
    ) : null;
    // 동기화 확인 탭. 패널이 자체 조회/액션 lifecycle 을 소유한다(탭 전환 시 remount).
    if (context.currentTab === "sync") {
      return (
        <>
          <WorkspaceSyncPanel />
          {groupsManagerModal}
        </>
      );
    }
    const { pending, saving, groupModalMember, workspaceModalMember } = this.state;
    const snapshot = this.source.getSnapshot();
    const { total, loadState } = snapshot;
    const isLoading = loadState === "loading";
    const columns = createOrgMemberColumns({
      currentMemberUuid: this.currentMemberUuid,
      busy: saving,
      onSelectTier: this.handleSelectTier,
      onEditGroups: this.handleEditGroups,
      onEditWorkspaces: this.handleEditWorkspaces,
    });

    return (
      <>
        <ResourceBrowserLayout
          ariaLabel="멤버 목록"
          summaryCount={total}
          summaryLabel="멤버"
          summaryInFilter
          error={tableStripError(snapshot)}
          onRetry={this.handleRefreshClick}
        >
          {/* 목록 상단 띠. 결과 수(idle)와 새로고침. 행 선택이 없는 목록이라 액션은 없다. */}
          <div className={bulkActionBarStyles.host}>
            <BulkActionBar
              source={this.source}
              idleContent={<FilterResultCount count={total} unitLabel="멤버" />}
              rightContent={
                <IconButton
                  size="sm"
                  text={isLoading ? "로딩 중…" : "새로고침"}
                  variant="surface"
                  disabled={isLoading}
                  onClick={this.handleRefreshClick}
                />
              }
              ariaLabel="멤버 목록 도구"
            />
            <HatDataTable
              source={this.source}
              columns={columns}
              emptyMessage="표시할 멤버가 없습니다."
              loadingMessage="멤버를 불러오는 중입니다."
              ariaLabel="멤버 테이블"
              scrollable
              minWidth="980px"
            />
          </div>
        </ResourceBrowserLayout>
        {pending ? (
          <HatTextModal
            open
            label="멤버 역할을 변경합니다."
            eyebrow={pending.member.githubLogin}
            caption={renderPendingCaption(pending)}
            error={pending.toTier === "admin"}
            okText="변경"
            cancelText="취소"
            busy={saving}
            onOkPress={this.handleConfirmPending}
            onCancelPress={this.handleCancelPending}
          />
        ) : null}
        {groupModalMember ? (
          <GroupMappingModal
            member={groupModalMember}
            groups={this.groups}
            groupsStatus={this.groupsStatus}
            onClose={this.handleCloseGroupModal}
            onChanged={this.handleGroupsChanged}
          />
        ) : null}
        {workspaceModalMember && workspaceModalMember.memberUuid !== null ? (
          <WorkspaceMappingModal
            memberUuid={workspaceModalMember.memberUuid}
            githubLogin={workspaceModalMember.githubLogin}
            memberGroups={workspaceModalMember.registryGroups}
            onClose={this.handleCloseWorkspaceModal}
            onChanged={this.handleWorkspacesChanged}
          />
        ) : null}
        {groupsManagerModal}
      </>
    );
  }
}

// 변경 요약(현재 → 대상). admin 은 모든 권한을 가진다. 그래서 admin 부여에는 경고 한 줄을
// 추가한다(danger 톤과 함께).
function renderPendingCaption(pending: PendingTierChange): ReactNode {
  return (
    <span className={styles.confirmCaption}>
      <Text variant={hatcheryTextModes["body-normal"]}>
        {`${memberTierLabel(pending.fromTier)} → ${memberTierLabel(pending.toTier)} (으)로 변경합니다.`}
      </Text>
      {pending.toTier === "admin" ? (
        <Text variant={hatcheryTextModes["body-bold"]} className={styles.confirmWarning}>
          관리자는 멤버 역할 변경을 포함한 전체 권한을 갖습니다. 부여하기 전에 내용을 확인해 주세요.
        </Text>
      ) : null}
    </span>
  );
}

function resolveErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  return "멤버 요청을 완료하지 못했습니다.";
}
